import React from 'react';
import { Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NearMeIcon from '@mui/icons-material/NearMe';
import InventoryIcon from '@mui/icons-material/Inventory';
import orderDetailsCollectorIdIcon from '../../../assets/svgs/orderDetailsCollectorIdIcon.svg';
import { getDescriptionForCollectorStatus } from '../types/CollectorStatus';

export interface OrderActivityRowProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  backgroundColor?: string;
  iconBubbleBackgroundColor?: string;
  iconColorOverride?: string;
  isRequiredAction?: boolean;
  showChevron?: boolean;
  subtitleColor?: string;
  hasAction?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  // Optional helpers for auto icon mapping
  statusKey?: string;
  collectorStatusKey?: string;
  collectorId?: string;
  eircode?: string;
}

export const OrderActivityRow = React.forwardRef<HTMLDivElement, OrderActivityRowProps>(
  (
    {
      icon,
      title,
      subtitle,
      backgroundColor,
      iconBubbleBackgroundColor,
      iconColorOverride,
      isRequiredAction,
      showChevron,
      subtitleColor,
      hasAction,
      className,
      onClick,
      statusKey,
      collectorStatusKey,
      collectorId,
      eircode,
    },
    ref
  ) => {
    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      if (hasAction) {
        e.currentTarget.style.opacity = '0.9';
      }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
      if (hasAction) {
        e.currentTarget.style.opacity = '1';
      }
    };

    const effectiveSubtitleColor =
      subtitleColor ||
      (isRequiredAction ? 'rgba(255,255,255,0.8)' : '#6E7191');

    const renderIconBubble = (
      inner: React.ReactNode,
      opts?: { bg?: string; color?: string; border?: string }
    ) => {
      return (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: iconBubbleBackgroundColor ?? opts?.bg ?? '#F2F4F7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            border: opts?.border,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: iconColorOverride ?? opts?.color ?? '#14142B',
              width: 20,
              height: 20,
            }}
          >
            {inner}
          </div>
        </div>
      );
    };

    const getCollectorStatusDisplayString = (): string => {
      if (!collectorStatusKey) return '';
      return getDescriptionForCollectorStatus(collectorStatusKey, collectorId || '');
    };

    const getAutoIcon = (): React.ReactNode => {
      // Collector status mapping
      if (collectorStatusKey) {
        switch (collectorStatusKey) {
          case 'WAITING_FOR_COLLECTOR_TO_ACCEPT':
            return renderIconBubble(<AccessTimeIcon sx={{ fontSize: 18 }} />);
          case 'COLLECTOR_ASSIGNED':
            return renderIconBubble(<CheckIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />, {
              bg: '#22C55E',
              color: '#FFFFFF'
            });
          case 'EN_ROUTE_TO_MERCHANT':
            return renderIconBubble(<ArrowForwardIcon sx={{ fontSize: 18 }} />);
          case 'COLLECTOR_NEAR_MERCHANT':
            return renderIconBubble(<NearMeIcon sx={{ fontSize: 18 }} />);
          case 'WAITING_AT_MERCHANT':
            return renderIconBubble(<AccessTimeIcon sx={{ fontSize: 18 }} />);
          case 'EN_ROUTE_TO_MANNA_BASE':
            return renderIconBubble(<ArrowForwardIcon sx={{ fontSize: 18 }} />);
          case 'COLLECTOR_NEAR_LZ':
            return renderIconBubble(<NearMeIcon sx={{ fontSize: 18 }} />);
          case 'AT_MANNA_BASE':
            return renderIconBubble(<InventoryIcon sx={{ fontSize: 18 }} />);
          case 'HANDED_OVER':
            return renderIconBubble(
              <img src={orderDetailsCollectorIdIcon} alt="Collector" style={{ width: 18, height: 18 }} />
            );
          default:
            return renderIconBubble(<ArrowForwardIcon sx={{ fontSize: 18 }} />);
        }
      }

      // Order status mapping
      switch (statusKey) {
        case 'CREATED': // New order -> star
          return renderIconBubble(<StarBorderIcon sx={{ fontSize: 18 }} />);
        case 'ACCEPTED': // Manna accepted -> arrow right
          return renderIconBubble(<ArrowForwardIcon sx={{ fontSize: 18 }} />);
        case 'STORE_ACCEPTED': // green bg check
          return renderIconBubble(<CheckIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />, {
            bg: '#22C55E',
            color: '#FFFFFF'
          });
        case 'STORE_PREPARED': // arrow right
          return renderIconBubble(<ArrowForwardIcon sx={{ fontSize: 18 }} />);
        case 'READY':
        case 'TAKE_OFF':
        case 'TAKING_OFF': // arrow up into cloud
          return renderIconBubble(<CloudUploadIcon sx={{ fontSize: 18 }} />);
        case 'DISPATCHED': // Flying to {Eircode} -> Cloud
          return renderIconBubble(<CloudIcon sx={{ fontSize: 18 }} />);
        case 'DELIVERED': // cloud and down arrow
          return renderIconBubble(<CloudDownloadIcon sx={{ fontSize: 18 }} />);
        default:
          // default: black svg and light grey background
          return renderIconBubble(<ArrowForwardIcon sx={{ fontSize: 18 }} />);
      }
    };

    const finalIcon = icon ?? getAutoIcon();
    // Always use generated collector status string when collectorStatusKey is provided
    const displayTitle = collectorStatusKey 
      ? getCollectorStatusDisplayString() 
      : title;

    return (
      <div
        ref={ref}
        onClick={onClick}
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginTop: 8,
          marginLeft: 2,
          marginRight: 2,
          padding: '8px 12px',
          cursor: hasAction ? 'pointer' : 'default',
          ...(backgroundColor && {
            backgroundColor,
            borderRadius: 8,
          }),
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Icon */}
        {finalIcon}

        {/* Text */}
        <div style={{ flex: 1 }}>
          <Typography
            variant="body1"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: isRequiredAction ? 'white' : '#14142B',
            }}
          >
            {displayTitle}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: effectiveSubtitleColor,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </div>

        {/* Chevron for required actions or interactive rows */}
        {showChevron && (
          <ChevronRightIcon
            style={{
              color: isRequiredAction ? 'white' : '#14142B',
              fontSize: 20,
            }}
          />
        )}
      </div>
    );
  }
);

OrderActivityRow.displayName = 'OrderActivityRow';



